import { api } from '@/global/services/api'
import { getBooleanFromValue, isArray } from '@/global/utils/utils'

const COLUMNS_COUNT = 4

// The tab talks to the vehicle's own winter service endpoints rather than to the vehicle
// endpoint every other editable tab saves through - see `winterServiceApiConfig`.
const WINTER_SERVICE_TAB_KEY = 'winterService'
const CODEBOOK_MODULE = 'road-maintenance'
const BASES_AUTOCOMPLETE_ROUTE = 'winter-service-bases/autocomplete'

// The lists this tab picks from. The road maintenance `codebook` endpoint answers keyed
// by the lowercased type name, which is how they are read back below.
const CODEBOOK_TYPES = [
  'WS_VEHICLE_TYPE',
  'WS_OWNERSHIP',
  'WS_CLEANING_TYPE',
  'WS_THROW_SIDE',
  'WS_DRIVE_TYPE',
  'WS_SPREAD_TYPE'
]

// The two ownerships. Only a subcontracted vehicle has a subcontractor and an operator;
// one of Zagrebacke ceste has neither, which is why they are emptied and greyed out for
// it. An ownership not yet picked greys out nothing - the choice is still open.
const SUBCONTRACTED = 'SUBCONTRACTED'
const ZG_ROADS = 'ZG_ROADS'

/**
 * The winter service configuration of one vehicle.
 *
 * Everything a winter service task reads off its vehicle is set up here - the vehicle
 * type, the drive, the throw side, the materials it may spread and how much of them, who
 * owns it and out of which base it works. The tasks only point at a registration, so this
 * tab is the single place any of it is decided.
 *
 * It is the one editable tab that does not save through the vehicle endpoint: the
 * configuration lives in the road maintenance module and has endpoints of its own, so the
 * tab fetches and saves itself and carries its own edit buttons. The tab only exists for
 * a vehicle already marked `is_winter_service` - there is nothing to configure otherwise,
 * and nothing to hang it on while the vehicle is still being created.
 */
export default {
  name: 'WinterService',

  data () {
    return {
      winterServiceCodebooks: {},
      // Every base, each with the subcontractors stationed at it and their operators.
      // Fetched whole and once: the base and the subcontractor narrow each other in both
      // directions, and doing that off one list keeps either of them free to be picked
      // first without a further request.
      winterServiceBases: [],
      // Mirrored out of the form: the config is built before the fields are drawn, so
      // what it has to decide (which fields are greyed out, what the two provider fields
      // offer) cannot be read off the form data itself.
      winterServiceEdit: false,
      winterServiceOwnershipId: null,
      winterServiceBaseId: null,
      winterServiceSubcontractorId: null
    }
  },

  computed: {
    // Turning the switch on the basic information tab off hides the tab again; what was
    // configured stays on the record, so turning it back on brings it back as it was.
    winterServiceVisible () {
      return getBooleanFromValue(this.formData?.item?.is_winter_service) &&
        getBooleanFromValue(this.permissions?.canReadVehicleWinterService)
    },

    // Not `canEdit`: the configuration has permissions of its own, since it lives in the
    // road maintenance module rather than on the vehicle.
    winterServiceCanEdit () {
      return getBooleanFromValue(this.permissions?.canUpdateVehicleWinterService)
    },

    winterServiceOwnershipCode () {
      return this.winterServiceCodebookItems('WS_OWNERSHIP')
        .find(item => Number(item.id) === Number(this.winterServiceOwnershipId))?.code ?? null
    },

    // Only an explicit Zagrebacke ceste closes the two provider fields. An ownership not
    // picked yet leaves them open - picking a subcontractor is itself what says the
    // vehicle is a subcontracted one.
    winterServiceIsZgRoads () {
      return this.winterServiceOwnershipCode === ZG_ROADS
    },

    winterServiceSubcontractedOwnershipId () {
      return this.winterServiceCodebookItems('WS_OWNERSHIP')
        .find(item => item.code === SUBCONTRACTED)?.id ?? null
    },

    winterServiceBaseItems () {
      return this.winterServiceBases.map(base => ({
        id: Number(base.id),
        name: base.name
      }))
    },

    // Every subcontractor there is, each remembering the base it works out of - that is
    // what lets a subcontractor picked first fill the base in.
    winterServiceAllSubcontractors () {
      return this.winterServiceBases.flatMap(base => (isArray(base?.subcontractors) ? base.subcontractors : [])
        .map(subcontractor => ({
          ...subcontractor,
          id: Number(subcontractor.id),
          baseId: Number(base.id)
        })))
    },

    // Narrowed to the picked base, and the whole list while none is picked - so either
    // field can be the first one filled in.
    winterServiceSubcontractorItems () {
      if (!this.winterServiceBaseId) {
        return this.winterServiceAllSubcontractors
      }

      return this.winterServiceAllSubcontractors
        .filter(subcontractor => subcontractor.baseId === Number(this.winterServiceBaseId))
    },

    // Only the operators of the picked subcontractor work for it, so those are the only
    // ones on offer.
    winterServiceOperatorItems () {
      const subcontractor = this.winterServiceAllSubcontractors
        .find(item => item.id === Number(this.winterServiceSubcontractorId))

      return isArray(subcontractor?.operators) ? subcontractor.operators : []
    },

    winterServiceApiConfig () {
      const vehicleId = this.$route?.params?.id

      return {
        get: {
          module: 'fleet',
          route: `vehicles/${vehicleId}/winter-service`
        },
        // `usePatch` off and `_method` set by hand: the layout spoofs PATCH by default,
        // and the configuration is upserted through a PUT.
        post: {
          module: 'fleet',
          route: `vehicles/${vehicleId}/winter-service`,
          usePatch: false
        }
      }
    },

    winterServiceEditConfig () {
      return {
        title: this.$t('fleet/vehicle/winter_service_tab.tab_label'),
        key: WINTER_SERVICE_TAB_KEY,
        apiConfig: this.winterServiceApiConfig,
        onDataReceived: (apiData) => this.applyWinterServiceForm(apiData?.data),
        afterSave: (context, data) => context?.refreshData?.(this.toWinterServiceForm(data)),
        // Cancelling puts the fetched configuration back into the form, so the three
        // values mirrored out of it have to go back with it - otherwise the base and the
        // two provider lists would keep narrowing by what was abandoned.
        afterCancel: (context) => this.syncWinterServiceMirrors(context?.getFormData?.()?.data),
        modifyDataBeforeSend: ({ copiedData }) => this.toWinterServicePayload(copiedData),
        // The tab saves itself, so the header above the tab strip - which posts every
        // other editable tab to the vehicle endpoint at once - has nothing to offer here.
        onTabChange: () => {
          this.showMainEditButtons = false
        },
        editUpdate: (event) => {
          const { edit } = event || {}
          this.winterServiceEdit = !!edit
        },
        tabContent: [
          {
            component: this.tabActionsComponent,
            title: this.$t('fleet/vehicle/winter_service_tab.tab_label'),
            showEditButtons: this.winterServiceCanEdit,
            showBack: false,
            visible: true
          },
          {
            component: this.columnsContentComponent,
            rows: [
              {
                columnsCount: COLUMNS_COUNT,
                fields: this.winterServiceFields
              }
            ]
          }
        ]
      }
    },

    winterServiceFields () {
      const providerField = (key, label, items, onChange) => ({
        key,
        label,
        type: 'autocomplete',
        items,
        autocomplete_options: {
          // Both lists come out of the bases already fetched whole - there is nothing to
          // look up remotely.
          allowSearch: false
        },
        onChange,
        creatable: true,
        // A vehicle of Zagrebacke ceste has neither, so both are left empty and greyed
        // out rather than offering a choice the endpoint would refuse. Anything else -
        // a subcontracted vehicle, or one whose ownership is still open - keeps them.
        editable: !this.winterServiceIsZgRoads,
        visible: true
      })

      return [
        this.winterServiceCodebookField('vehicle_type_id', this.$t('fleet/vehicle/winter_service_tab.vehicle_type'), 'WS_VEHICLE_TYPE'),
        this.winterServiceCodebookField('ownership_id', this.$t('fleet/vehicle/winter_service_tab.ownership'), 'WS_OWNERSHIP', {
          onChange: ({ selectedItem, updateData }) => {
            this.winterServiceOwnershipId = selectedItem?.id ?? selectedItem

            if (!this.winterServiceIsZgRoads) {
              return
            }

            // Nothing to keep them for any more, and the endpoint refuses both on a
            // vehicle of Zagrebacke ceste. The base stays: it is where the vehicle works
            // out of either way.
            this.winterServiceSubcontractorId = null
            updateData?.({
              subcontractor_id: null,
              operator_id: null
            })
          }
        }),
        {
          key: 'base_id',
          label: this.$t('fleet/vehicle/winter_service_tab.base'),
          type: 'autocomplete',
          items: this.winterServiceBaseItems,
          autocomplete_options: {
            allowSearch: false
          },
          onChange: ({ selectedItem, updateData }) => {
            this.winterServiceBaseId = selectedItem?.id ?? selectedItem

            // A vehicle of Zagrebacke ceste has no subcontractor and no operator at all,
            // so the base fills in neither - both stay empty and greyed out.
            if (this.winterServiceIsZgRoads) {
              return
            }

            // The base narrows the subcontractors, so the one standing there has to still
            // be one of them: it is kept when it is, replaced when the base leaves exactly
            // one choice, and cleared when it leaves several.
            const [only] = this.winterServiceSubcontractorItems
            const stillValid = this.winterServiceSubcontractorItems
              .some(item => item.id === Number(this.winterServiceSubcontractorId))

            if (stillValid) {
              return
            }

            const subcontractorId = this.winterServiceSubcontractorItems.length === 1
              ? only.id
              : null

            this.winterServiceSubcontractorId = subcontractorId
            updateData?.({
              subcontractor_id: subcontractorId,
              operator_id: null
            })
          },
          creatable: true,
          editable: true,
          visible: true
        },
        providerField(
          'subcontractor_id',
          this.$t('fleet/vehicle/winter_service_tab.subcontractor'),
          this.winterServiceSubcontractorItems,
          ({ selectedItem, updateData }) => {
            const subcontractorId = selectedItem?.id ?? selectedItem
            this.winterServiceSubcontractorId = subcontractorId

            const picked = this.winterServiceAllSubcontractors
              .find(item => item.id === Number(subcontractorId))

            // The other direction: a subcontractor works out of exactly one base, so
            // picking one fills the base in - and says the vehicle is a subcontracted one,
            // which is the only ownership the endpoint accepts a subcontractor under.
            if (picked?.baseId) {
              this.winterServiceBaseId = picked.baseId
            }

            if (picked && this.winterServiceSubcontractedOwnershipId !== null) {
              this.winterServiceOwnershipId = this.winterServiceSubcontractedOwnershipId
            }

            updateData?.({
              ...(picked?.baseId ? { base_id: picked.baseId } : {}),
              ...(picked && this.winterServiceSubcontractedOwnershipId !== null
                ? { ownership_id: this.winterServiceSubcontractedOwnershipId }
                : {}),
              // Its operators are not the previous subcontractor's.
              operator_id: null
            })
          }
        ),
        providerField(
          'operator_id',
          this.$t('fleet/vehicle/winter_service_tab.operator'),
          this.winterServiceOperatorItems
        ),
        {
          // Every material the vehicle may spread. A task then picks one out of these -
          // the plus opens the list, and each chosen one gets a chip of its own.
          key: 'spread_type_ids',
          type: 'select',
          multiple: true,
          chips: true,
          smallChips: true,
          items: this.winterServiceCodebookItems('WS_SPREAD_TYPE'),
          upperLabel: this.$t('fleet/vehicle/winter_service_tab.spread_types'),
          // SelectField reads its disabled state off its own config rather than off the
          // prop the renderer binds, so the tab's edit state has to be handed over here.
          disabled: !this.winterServiceEdit || !this.winterServiceCanEdit,
          colSpan: 2,
          creatable: true,
          editable: true,
          visible: true
        },
        {
          key: 'spread_capacity_min',
          label: this.$t('fleet/vehicle/winter_service_tab.spread_capacity_min'),
          field_type: 'number',
          creatable: true,
          editable: true,
          visible: true
        },
        {
          key: 'spread_capacity_max',
          label: this.$t('fleet/vehicle/winter_service_tab.spread_capacity_max'),
          field_type: 'number',
          creatable: true,
          editable: true,
          visible: true
        },
        this.winterServiceCodebookField('cleaning_type_id', this.$t('fleet/vehicle/winter_service_tab.cleaning_type'), 'WS_CLEANING_TYPE'),
        this.winterServiceCodebookField('throw_side_id', this.$t('fleet/vehicle/winter_service_tab.throw_side'), 'WS_THROW_SIDE'),
        this.winterServiceCodebookField('drive_type_id', this.$t('fleet/vehicle/winter_service_tab.drive_type'), 'WS_DRIVE_TYPE')
      ]
    }
  },

  created () {
    this.fetchWinterServiceCodebooks()
    this.fetchWinterServiceBases()
  },

  methods: {
    async fetchWinterServiceCodebooks () {
      try {
        const { data } = await api(CODEBOOK_MODULE).get('codebook', { types: CODEBOOK_TYPES })
        this.winterServiceCodebooks = data || {}
      }
      catch (exception) {
        console.error(exception)
      }
    },

    /**
     * The entries of one codebook type as the fields want them: `{ id, name }`, the name
     * being the human label. This endpoint keeps that label as a translation key on
     * `description` and hands it over untranslated, so it is resolved here.
     */
    winterServiceCodebookItems (typeName) {
      const entries = this.winterServiceCodebooks?.[typeName.toLowerCase()]

      if (!isArray(entries)) {
        return []
      }

      return entries.map(entry => ({
        id: Number(entry.id),
        name: entry.description ? this.$t(entry.description) : entry.name,
        // Kept so the ownership can be told apart without another lookup.
        code: entry.name
      }))
    },

    winterServiceCodebookField (key, label, typeName, extra = {}) {
      return {
        key,
        label,
        type: 'autocomplete',
        items: this.winterServiceCodebookItems(typeName),
        autocomplete_options: {
          allowSearch: false
        },
        creatable: true,
        editable: true,
        visible: true,
        ...extra
      }
    },

    /**
     * Every base, with the subcontractors stationed at it and their operators. Fetched
     * whole rather than searched: the base and the subcontractor narrow each other in
     * both directions, so the whole list has to be at hand however the user starts.
     */
    async fetchWinterServiceBases () {
      try {
        // `include_all` in snake case: this asks the endpoint directly rather than
        // through the autocomplete field, and that is the key the endpoint reads.
        const { data } = await api(CODEBOOK_MODULE).get(BASES_AUTOCOMPLETE_ROUTE, {
          query: '',
          include_all: 1
        })
        this.winterServiceBases = isArray(data) ? data : []
      }
      catch (exception) {
        console.error(exception)
      }
    },

    /**
     * The endpoint reports the configuration nested - every codebook and every provider
     * as its own object - and the fields edit it flat.
     */
    toWinterServiceForm (record) {
      return {
        ...record,
        vehicle_type_id: record?.vehicle_type?.id ?? null,
        ownership_id: record?.ownership?.id ?? null,
        base_id: record?.base?.id ?? null,
        subcontractor_id: record?.subcontractor?.id ?? null,
        operator_id: record?.operator?.id ?? null,
        cleaning_type_id: record?.cleaning_type?.id ?? null,
        throw_side_id: record?.throw_side?.id ?? null,
        drive_type_id: record?.drive_type?.id ?? null,
        spread_type_ids: (isArray(record?.spread_types) ? record.spread_types : [])
          .map(entry => Number(entry?.id))
          .filter(id => !!id)
      }
    },

    /**
     * Writes the flat keys onto the record itself. The layout hands `onDataReceived` the
     * very object it then turns into the form data, so the flat values have to land on it
     * rather than on a copy - that also keeps the untouched copy it restores on cancel
     * flat, instead of emptying every field.
     */
    applyWinterServiceForm (record) {
      if (!record) {
        return record
      }

      Object.assign(record, this.toWinterServiceForm(record))
      this.syncWinterServiceMirrors(record)

      return record
    },

    /**
     * What the field list needs before it is next rebuilt: which fields are greyed out and
     * what the base and the two provider fields offer all follow from these three, and the
     * list is built before the form data it would read them off exists.
     */
    syncWinterServiceMirrors (record) {
      if (!record) {
        return
      }

      this.winterServiceOwnershipId = record.ownership_id ?? null
      this.winterServiceBaseId = record.base_id ?? null
      this.winterServiceSubcontractorId = record.subcontractor_id ?? null
    },

    toWinterServicePayload (data) {
      return {
        ...data,
        // The layout posts as FormData and spoofs PATCH unless told otherwise; the
        // configuration is upserted through a PUT.
        _method: 'PUT'
      }
    }
  }
}
